import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import CopyIcon from '../../../../assets/svg/duplicate.svg';
import ThumbsUp from '../../../../assets/svg/thumbs-up.svg';
import ThumbsDown from '../../../../assets/svg/thumbs-down.svg';
import RobotIcon from '../../../../assets/svg/ai-agent-robot.svg';

const GROUP_THRESHOLD_MS = 2 * 60 * 1000;

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateLabel = (timestamp, labels) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return labels.get('separator.today');
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return labels.get('separator.yesterday');
  }
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const isLastInGroup = (message, nextMessage) => {
  if (!nextMessage) {
    return true;
  }
  if (nextMessage.get('role') !== message.get('role')) {
    return true;
  }
  const gap = (nextMessage.get('timestamp') || 0) - (message.get('timestamp') || 0);
  return gap > GROUP_THRESHOLD_MS;
};

const markdownComponents = {
  table: ({ children, ...props }) => (
    <div className="ais-chat-table-wrapper">
      <table {...props}>{children}</table>
    </div>
  ),
};

const IMAGE_TAG_OPEN = '[[TPO_IMAGE]]';
const IMAGE_TAG_CLOSE = '[[/TPO_IMAGE]]';
// svg+xml is included so vector charts (crisp at any zoom level) render the same as raster ones -
// see docs/chart_rendering_notes.md item 2.
const IMAGE_DATA_URL_REGEX = /^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,[A-Za-z0-9+/]+=*$/i;

// Splits assistant message text into plain-text/markdown segments and embedded base64 image segments
// so images can be rendered as actual <img> elements instead of raw text inside the markdown.
const splitMessageContent = (text) => {
  const parts = [];
  let cursor = 0;

  if (!text) {
    return parts;
  }

  while (cursor < text.length) {
    const openIndex = text.indexOf(IMAGE_TAG_OPEN, cursor);
    if (openIndex === -1) {
      parts.push({ type: 'text', content: text.slice(cursor) });
      break;
    }

    if (openIndex > cursor) {
      parts.push({ type: 'text', content: text.slice(cursor, openIndex) });
    }

    const contentStart = openIndex + IMAGE_TAG_OPEN.length;
    const closeIndex = text.indexOf(IMAGE_TAG_CLOSE, contentStart);
    if (closeIndex === -1) {
      // Closing tag hasn't streamed in yet - show a loading placeholder instead of raw base64 text.
      parts.push({ type: 'image-loading' });
      break;
    }

    const dataUrl = text.slice(contentStart, closeIndex).trim();
    parts.push(IMAGE_DATA_URL_REGEX.test(dataUrl) ? { type: 'image', content: dataUrl } : { type: 'text', content: text.slice(openIndex, closeIndex + IMAGE_TAG_CLOSE.length) });
    cursor = closeIndex + IMAGE_TAG_CLOSE.length;
  }

  return parts;
};

/**
 * Renders a scrollable list of chat message bubbles (markdown/images, date separators,
 * copy/feedback actions). Shared between the live chat view and the read-only history
 * transcript view so both render identically.
 */
export default function AiAssistantMessageList({
  messages,
  labels,
  isVisible = true,
  showCopy = true,
  showFeedback = true,
  onFeedback,
  emptyState = null,
}) {
  const [copiedId, setCopiedId] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!isVisible || !messagesEndRef.current) {
      return;
    }
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [isVisible, messages]);

  // Chart images are a fixed-resolution raster/vector image with no built-in way to enlarge -
  // the chat's own "A+" control only scales text. This gives them a dedicated click-to-zoom
  // affordance (see docs/chart_rendering_notes.md item 1).
  useEffect(() => {
    if (!zoomedImage) {
      return;
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setZoomedImage(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [zoomedImage]);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const renderMessage = (message, index) => {
    const role = message.get('role');
    const text = message.get('text');
    const timestamp = message.get('timestamp');
    const isStreaming = message.get('streaming');
    const isIncomplete = message.get('incomplete');

    if (isStreaming && !text) {
      return null;
    }

    const prevTimestamp = index > 0 ? messages.get(index - 1).get('timestamp') : null;
    const nextMessage = messages.get(index + 1);

    const showDateSeparator = timestamp && (
      !prevTimestamp ||
      new Date(timestamp).toDateString() !== new Date(prevTimestamp).toDateString()
    );
    const showTime = timestamp && !isStreaming && isLastInGroup(message, nextMessage);

    const copyBtn = showCopy && !isStreaming && (
      <button
        className={`ais-chat-copy-btn${copiedId === index ? '--copied' : ''}`}
        onClick={() => handleCopy(text, index)}
        title={labels.get('message.button.copy')}
        aria-label={labels.get('message.button.copy')}
      >
        {copiedId === index ? <i className="fa fa-check" /> : <CopyIcon />}
      </button>
    );

    const feedback = message.get('feedback');
    const feedbackBtns = showFeedback && !isStreaming && role === 'assistant' && (
      <>
        <button
          className={feedback === 'up' ? 'ais-chat-feedback-btn--active' : 'ais-chat-feedback-btn'}
          onClick={() => onFeedback(index, 'up')}
          title={labels.get('message.button.goodResponse')}
          aria-label={labels.get('message.button.goodResponse')}
        >
          <ThumbsUp/>
        </button>
        <button
          className={feedback === 'down' ? 'ais-chat-feedback-btn--active' : 'ais-chat-feedback-btn'}
          onClick={() => onFeedback(index, 'down')}
          title={labels.get('message.button.badResponse')}
          aria-label={labels.get('message.button.badResponse')}
        >
          <ThumbsDown/>
        </button>
      </>
    );

    return (
      <React.Fragment key={index}>
        {showDateSeparator && (
          <div className="ais-chat-date-separator">
            <span>{formatDateLabel(timestamp, labels)}</span>
          </div>
        )}
        <div className={`ais-chat-message ais-chat-message--${role}`}>
          <div className="ais-chat-message-content">
            <div className="ais-chat-bubble-row">
              {role === 'user' && copyBtn}
              {role === 'assistant' && (
                <div className='ais-chat-bubble-assistant-icon'><RobotIcon/></div>
              )}
              <div className="ais-chat-bubble">
                {role === 'assistant' ? (
                  <>
                    {splitMessageContent(text).map((part, partIndex) => {
                      if (part.type === 'image') {
                        return (
                          <button
                            key={partIndex}
                            type="button"
                            className="ais-chat-image-btn"
                            onClick={() => setZoomedImage(part.content)}
                            title={labels.get('image.button.enlarge', 'Click to enlarge')}
                            aria-label={labels.get('image.button.enlarge', 'Click to enlarge')}
                          >
                            <img className="ais-chat-image" src={part.content} alt="" />
                            <span className="ais-chat-image-zoom-hint"><i className="fa fa-search-plus" /></span>
                          </button>
                        );
                      }
                      if (part.type === 'image-loading') {
                        return (
                          <div key={partIndex} className="ais-chat-image-loading">
                            <span className="ais-chat-typing-dot" />
                            <span className="ais-chat-typing-dot" />
                            <span className="ais-chat-typing-dot" />
                          </div>
                        );
                      }
                      return (
                        <ReactMarkdown
                          key={partIndex}
                          remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
                          rehypePlugins={[rehypeKatex]}
                          components={markdownComponents}
                        >
                          {part.content}
                        </ReactMarkdown>
                      );
                    })}
                    {isStreaming && text && <span className="ais-chat-cursor" />}
                  </>
                ) : (
                  text
                )}
              </div>
              {role === 'assistant' && (
                <div className="ais-chat-bubble-actions">
                  {copyBtn}
                  {feedbackBtns}
                </div>
              )}
            </div>
            {isIncomplete && (
              <span className="ais-chat-message-incomplete">{labels.get('message.incomplete', 'Response was cut off')}</span>
            )}
            {showTime && (
              <span
                className="ais-chat-message-time"
                title={new Date(timestamp).toLocaleString()}
              >
                {formatTime(timestamp)}
              </span>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  return (
    <>
      <div className="ais-chat-messages">
        {messages.size === 0 ? emptyState : messages.map((message, index) => renderMessage(message, index))}
        <div ref={messagesEndRef}/>
      </div>
      {zoomedImage && (
        <div className="ais-chat-image-lightbox" onClick={() => setZoomedImage(null)}>
          <button
            type="button"
            className="ais-chat-image-lightbox-close"
            onClick={() => setZoomedImage(null)}
            title={labels.get('image.button.close', 'Close')}
            aria-label={labels.get('image.button.close', 'Close')}
          >
            <i className="fa fa-times" />
          </button>
          <img
            className="ais-chat-image-lightbox-img"
            src={zoomedImage}
            alt=""
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

AiAssistantMessageList.propTypes = {
  messages: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  isVisible: PropTypes.bool,
  showCopy: PropTypes.bool,
  showFeedback: PropTypes.bool,
  onFeedback: PropTypes.func,
  emptyState: PropTypes.node,
};
